const qualifymeAPI =
  process.env.REACT_APP_API_BASE_URL ?? "https://api.qualifyme.ai";

const loginDocs = {
  title: "Login",
  method: "POST",
  path: "/oauth/token",
  description:
    "This endpoint allows you to get the access token for the QualifyMe API using OAuth2 client credentials flow",
  requiredAttributes: [
    {
      name: "grant_type",
      type: "string",
      description: "The OAuth grant type. Must be 'client_credentials'",
    },
    {
      name: "client_id",
      type: "string",
      description: "Your OAuth client ID",
    },
    {
      name: "client_secret",
      type: "string",
      description: "Your OAuth client secret",
    },
  ],
  optionalAttributes: [],
  request: `curl --location '${qualifymeAPI}/oauth/token' \\
--header 'Content-Type: application/json' \\
--data '{
  "grant_type": "client_credentials",
  "client_id": "your_client_id",
  "client_secret": "your_client_secret"
}'`,
  response: `{
  "access_token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
  "token_type": "Bearer",
  "expires_in": 3600,
  "scope": "read:jobs read:applications read:candidates"
}`,
};

const getAllJobPostsDocs = {
  title: "Retrieve all job posts",
  method: "GET",
  path: "/api/job-posts",
  description:
    "This endpoint allows you to retrieve all job posts with optional pagination and filtering",
  requiredAttributes: [],
  optionalAttributes: [
    {
      name: "page",
      type: "number",
      description: "The page number to retrieve (default: 1)",
    },
    {
      name: "limit",
      type: "number",
      description: "The number of job posts to retrieve per page (default: 10)",
    },
    {
      name: "isActive",
      type: "boolean",
      description:
        "Filter by active status. If true, returns only active job posts",
    },
  ],
  request: `curl --location '${qualifymeAPI}/api/job-posts' \\
--header 'Authorization: Bearer {your_token}'`,
  response: `{
  "data": [
    {
      "_id": "695bb8f40e4e3a38a3c6dd78",
      "title": "Full Stack Developer",
      "description": "We are looking for a full stack developer to join our team.",
      "position": "Full Stack Developer",
      "slug": "full-stack-developer-ne72f",
      "isActive": false,
      "numberOfOpenings": 1,
      "currency": "INR",
      "endDate": null,
      "createdAt": "2026-01-05T13:13:24.811Z",
      "updatedAt": "2026-01-06T06:27:16.924Z"
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 10,
    "total": 25,
    "totalPages": 3
  }
}`,
};

const getJobPostByIdDocs = {
  title: "Retrieve a job post by id",
  method: "GET",
  path: "/api/job-posts/:id",
  description:
    "This endpoint allows you to retrieve a specific job post by its ID",
  requiredAttributes: [
    {
      name: "id",
      type: "string",
      description: "The unique identifier of the job post",
    },
  ],
  optionalAttributes: [],
  request: `curl --location '${qualifymeAPI}/api/job-posts/{id}' \\
--header 'Authorization: Bearer {your_token}'`,
  response: `{
  "_id": "694a7d6e43943cf24a15737f",
  "title": "Software Engineer",
  "description": "We are looking for an entry-level Software Engineer to join our team. This position offers on-the-job training and the opportunity to work under the mentorship of senior engineers. Fresh graduates with a passion for programming and learning are encouraged to apply.",
  "position": "Software Engineer (Entry Level)",
  "slug": "software-engineer-a75do",
  "isActive": false,
  "numberOfOpenings": 1,
  "currency": "INR",
  "endDate": null,
  "requirements": [
    {
      "id": 0,
      "topic": "",
      "description": "Basic programming skills to develop software solutions.",
      "mustMatch": true
    },
    {
      "id": 1,
      "topic": "",
      "description": "Effective communication skills to collaborate within the team.",
      "mustMatch": false
    },
    {
      "id": 2,
      "topic": "",
      "description": "Willingness to learn and adapt in a fast-paced environment.",
      "mustMatch": false
    },
    {
      "id": 3,
      "topic": "",
      "description": "Bachelor's degree in a relevant field such as Computer Science, Software Engineering, or related disciplines.",
      "mustMatch": true
    },
    {
      "id": 4,
      "topic": "",
      "description": "0-2 years of experience in software development or related internships.",
      "mustMatch": true
    },
    {
      "id": 5,
      "topic": "",
      "description": "Ability and enthusiasm to benefit from on-the-job training and mentorship by senior engineers.",
      "mustMatch": false
    }
  ],
  "createdAt": "2025-12-23T11:30:54.673Z",
  "updatedAt": "2025-12-26T09:31:13.671Z"
}`,
};

const getAllJobApplicationsDocs = {
  title: "Retrieve all job applications",
  method: "GET",
  path: "/api/job-applications",
  description:
    "This endpoint allows you to retrieve all job applications with optional pagination and filtering",
  requiredAttributes: [],
  optionalAttributes: [
    {
      name: "jobPostId",
      type: "string",
      description: "Filter applications by job post ID",
    },
    {
      name: "name",
      type: "string",
      description: "Filter applications by candidate name",
    },
    {
      name: "email",
      type: "string",
      description: "Filter applications by candidate email",
    },
    {
      name: "appliedDate",
      type: "string",
      description: "Filter applications by applied date",
    },
    {
      name: "jobDescriptionId",
      type: "string",
      description: "Filter applications by job description ID",
    },
    {
      name: "status",
      type: "string",
      description:
        "Filter applications by status (e.g., 'applied', 'rejected', 'shortlisted')",
    },
    {
      name: "page",
      type: "number",
      description: "The page number to retrieve (default: 1)",
    },
    {
      name: "limit",
      type: "number",
      description:
        "The number of applications to retrieve per page (default: 10)",
    },
  ],
  request: `curl --location '${qualifymeAPI}/api/job-applications' \\
--header 'Authorization: Bearer {your_token}'`,
  response: `{
  "data": [
    {
      "_id": "695e1ee9545d2d635034070a",
      "user": {
        "_id": "69296f3ee441a3cbe062a1dc",
        "firstName": "Jhon",
        "lastName": "Doe",
        "email": "jhondoe@example.com"
      },
      "jobPost": {
        "_id": "695b9c50771b88f94774eff6",
        "title": "Junior Engineer for WordPress and PHP Development"
      },
      "applied_date": "2026-01-07T08:52:57.808Z",
      "status": "rejected",
      "profileMatchStatus": {
        "status": "unqualified",
        "score": 41
      },
      "createdAt": "2026-01-07T08:52:57.811Z"
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 10,
    "total": 25,
    "totalPages": 3
  }
}`,
};

const getJobApplicationByIdDocs = {
  title: "Retrieve a job application by id",
  method: "GET",
  path: "/api/job-applications/:id",
  description:
    "This endpoint allows you to retrieve a specific job application by its ID",
  requiredAttributes: [
    {
      name: "id",
      type: "string",
      description: "The unique identifier of the job application",
    },
  ],
  optionalAttributes: [],
  request: `curl --location '${qualifymeAPI}/api/job-applications/{id}' \\
--header 'Authorization: Bearer {your_token}'`,
  response: `{
  "_id": "695e1ee9545d2d635034070a",
  "user": {
    "_id": "69296f3ee441a3cbe062a1dc",
    "firstName": "Jhon",
    "lastName": "Doe",
    "email": "jhondoe@example.com"
  },
  "jobPost": {
    "_id": "695b9c50771b88f94774eff6",
    "title": "Junior Engineer for WordPress and PHP Development"
  },
  "applied_date": "2026-01-07T08:52:57.808Z",
  "status": "rejected",
  "profileMatchStatus": {
    "status": "unqualified",
    "score": 41
  },
  "questionnaireStatus": "unqualified",
  "createdAt": "2026-01-07T08:52:57.811Z",
  "updatedAt": "2026-01-07T08:54:11.559Z"
}`,
};

const getAllCandidatesDocs = {
  title: "Retrieve all candidates",
  method: "GET",
  path: "/api/candidates",
  description:
    "This endpoint allows you to retrieve all candidates with optional pagination and filtering",
  requiredAttributes: [],
  optionalAttributes: [
    {
      name: "page",
      type: "number",
      description: "The page number to retrieve (default: 1)",
    },
    {
      name: "limit",
      type: "number",
      description:
        "The number of candidates to retrieve per page (default: 10)",
    },
    {
      name: "email",
      type: "string",
      description: "Filter candidates by email",
    },
    {
      name: "name",
      type: "string",
      description: "Filter candidates by name",
    },
    {
      name: "jobDescriptionId",
      type: "string",
      description: "Filter candidates by applied job post ID",
    },
  ],
  request: `curl --location '${qualifymeAPI}/api/candidates' \\
--header 'Authorization: Bearer {your_token}'`,
  response: `{
  "data": [
    {
      "_id": "6957a1de4e695044f7af6462",
      "firstName": "Jhon",
      "lastName": "Doe",
      "email": "jhondoe@example.com",
      "orgId": "689c38143b011e727e472e54",
      "createdAt": "2026-01-02T10:45:50.431Z"
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 20,
    "total": 226,
    "totalPages": 12
  }
}`,
};

const getCandidateByIdDocs = {
  title: "Retrieve a candidate by id",
  method: "GET",
  path: "/api/candidates/:id",
  description:
    "This endpoint allows you to retrieve a specific candidate by their ID",
  requiredAttributes: [
    {
      name: "id",
      type: "string",
      description: "The unique identifier of the candidate",
    },
  ],
  optionalAttributes: [],
  request: `curl --location '${qualifymeAPI}/api/candidates/{id}' \\
--header 'Authorization: Bearer {your_token}'`,
  response: `{
  "_id": "6957a1de4e695044f7af6462",
  "firstName": "Jhon",
  "lastName": "Doe",
  "email": "jhondoe@example.com",
  "orgId": "689c38143b011e727e472e54",
  "createdAt": "2026-01-02T10:45:50.431Z",
  "updatedAt": "2026-01-02T11:02:55.235Z"
}`,
};

// Legacy exports for backward compatibility
const jobPostsDocs = getAllJobPostsDocs;
const jobApplicationsDocs = getAllJobApplicationsDocs;
const candidatesDocs = getAllCandidatesDocs;

export {
  getAllJobPostsDocs,
  getJobPostByIdDocs,
  getAllJobApplicationsDocs,
  getJobApplicationByIdDocs,
  getAllCandidatesDocs,
  getCandidateByIdDocs,
  jobPostsDocs,
  jobApplicationsDocs,
  candidatesDocs,
  loginDocs,
};
